import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Modal, Field } from "./Rooms";
import { formatDate, formatCurrency } from "@/lib/format";
import PhoneInput from "@/components/PhoneInput";
import {
    Plus, Search, Star, Ban, Mail, MessageCircle, Phone, ClipboardList,
    Tag as TagIcon, RefreshCw, X, AlertTriangle, TrendingUp,
    Users as UsersIcon, Briefcase, Baby, ShieldCheck, Heart, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

// ------------------------------------------------------------
// Segment definitions (matches backend crm.py)
// ------------------------------------------------------------
const SEGMENT_KEYS = [
    { key: "all",        icon: UsersIcon,    color: "text-foreground" },
    { key: "frequent",   icon: RefreshCw,    color: "text-blue-700" },
    { key: "high_value", icon: TrendingUp,   color: "text-emerald-700" },
    { key: "vip",        icon: Star,         color: "text-amber-700" },
    { key: "corporate",  icon: Briefcase,    color: "text-violet-700" },
    { key: "family",     icon: Baby,         color: "text-pink-700" },
    { key: "ota",        icon: ShieldCheck,  color: "text-orange-700" },
    { key: "direct",     icon: Heart,        color: "text-orange-700" },
    { key: "inactive",   icon: ClipboardList,color: "text-muted-foreground" },
];

const SEGMENT_BADGE = {
    frequent:   "bg-blue-50 text-blue-700",
    high_value: "bg-emerald-50 text-emerald-700",
    vip:        "bg-amber-50 text-amber-800",
    corporate:  "bg-violet-50 text-violet-700",
    family:     "bg-pink-50 text-pink-700",
    ota:        "bg-orange-50 text-orange-700",
    direct:     "bg-orange-50 text-orange-700",
    inactive:   "bg-muted text-muted-foreground",
};

const LOYALTY_TIER_COLOR = {
    regular: "text-muted-foreground",
    silver: "text-muted-foreground",
    gold: "text-amber-600",
    platinum: "text-violet-700",
};

// ------------------------------------------------------------
// Main page
// ------------------------------------------------------------
export default function Guests() {
    const { t } = useTranslation();
    const [guests, setGuests] = useState([]);
    const [stats, setStats] = useState(null);
    const [filter, setFilter] = useState({ segment: "all", search: "", tag: "", vip: "", blacklisted: "" });
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(null);

    const load = async () => {
        const params = new URLSearchParams();
        if (filter.segment !== "all") params.set("segment", filter.segment);
        if (filter.search) params.set("search", filter.search);
        if (filter.tag) params.set("tag", filter.tag);
        if (filter.vip !== "") params.set("vip", filter.vip);
        if (filter.blacklisted !== "") params.set("blacklisted", filter.blacklisted);
        try {
            const [{ data: g }, { data: s }] = await Promise.all([
                api.get(`/guests?${params}`),
                api.get("/guests/crm/segments"),
            ]);
            setGuests(g); setStats(s);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.segment, filter.tag, filter.vip, filter.blacklisted]);

    const recompute = async () => {
        try {
            const { data } = await api.post("/guests/crm/recompute-all");
            toast.success(t("guests.recompute_toast", { n: data.guests }));
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const onSearch = (e) => { e.preventDefault(); load(); };

    return (
        <>
            <Topbar
                title={t("guests.title_full")}
                subtitle={statsSubtitle(stats, t)}
                actions={
                    <div className="flex items-center gap-2">
                        <button onClick={recompute} className="akm-btn-ghost" data-testid="crm-recompute">
                            <RefreshCw size={14} /> {t("guests.recompute")}
                        </button>
                        <button onClick={() => setOpen(true)} className="akm-btn-primary" data-testid="new-guest-btn">
                            <Plus size={14} /> {t("guests.new_guest")}
                        </button>
                    </div>
                }
            />
            <div className="flex-1 overflow-auto p-4 md:p-6 space-y-4">
                {stats && <SegmentTiles stats={stats} active={filter.segment} onPick={(k) => setFilter({ ...filter, segment: k })} />}

                <div className="flex flex-wrap items-center gap-2" data-testid="crm-filters">
                    <form onSubmit={onSearch} className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input data-testid="guest-search" value={filter.search}
                               onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                               className="akm-input pl-8 w-[260px]"
                               placeholder={t("guests.search_all")} />
                    </form>
                    <select className="akm-input max-w-[140px]" value={filter.vip}
                            onChange={(e) => setFilter({ ...filter, vip: e.target.value })}>
                        <option value="">{t("guests.vip_all")}</option>
                        <option value="true">{t("guests.vip_only")}</option>
                        <option value="false">{t("guests.vip_non")}</option>
                    </select>
                    <select className="akm-input max-w-[160px]" value={filter.blacklisted}
                            onChange={(e) => setFilter({ ...filter, blacklisted: e.target.value })}>
                        <option value="">{t("guests.status_all")}</option>
                        <option value="false">{t("guests.status_active")}</option>
                        <option value="true">{t("guests.status_blacklisted")}</option>
                    </select>
                    <div className="ml-auto text-[11px] text-muted-foreground">{t("guests.count_guests", { count: guests.length })}</div>
                </div>

                <GuestsTable guests={guests} onOpen={setDetail} onReload={load} />
            </div>

            {open && <GuestForm onClose={() => setOpen(false)} onSaved={async () => { setOpen(false); await load(); }} />}
            {detail && <GuestDrawer guestId={detail} onClose={() => setDetail(null)} onReload={load} />}
        </>
    );
}

function statsSubtitle(stats, t) {
    if (!stats) return t("guests.loading_ellipsis");
    const total = stats.total_guests || 0;
    // Manual singular/plural: "1 klient" vs "2 klientë" (Albanian).
    const clientWord = total === 1
        ? t("guests.client_singular", { defaultValue: "klient" })
        : t("guests.client_plural", { defaultValue: "klientë" });
    return t("guests.subtitle_stats", {
        total,
        client_word: clientWord,
        ltv: formatCurrency(stats.lifetime_value_total),
        vip: stats.vip,
        optin: stats.marketing_opt_in,
    });
}

// ------------------------------------------------------------
// Segment tiles row
// ------------------------------------------------------------
function SegmentTiles({ stats, active, onPick }) {
    const { t } = useTranslation();
    return (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-9" data-testid="crm-segments">
            {SEGMENT_KEYS.map((s) => {
                const Icon = s.icon;
                const n = s.key === "all" ? stats.total_guests : stats.segments?.[s.key] || 0;
                const isActive = active === s.key;
                return (
                    <button key={s.key} onClick={() => onPick(s.key)}
                            data-testid={`segment-${s.key}`}
                            className={`group rounded-md border p-3 text-left transition ${
                                isActive ? "border-[#ff7b02] bg-[#ff7b02]/10" : "border-border bg-card hover:border-[#ff7b02]/40"
                            }`}>
                        <div className="flex items-center justify-between">
                            <Icon size={14} className={s.color} />
                            <span className={`text-[18px] font-semibold tracking-tight ${s.color}`}>{n}</span>
                        </div>
                        <div className="mt-1 text-[11px] font-medium text-muted-foreground">{t(`guests.segments.${s.key}`)}</div>
                    </button>
                );
            })}
        </div>
    );
}

// ------------------------------------------------------------
// Guests table
// ------------------------------------------------------------
function GuestsTable({ guests, onOpen, onReload }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const canDelete = ["super_admin", "hotel_admin", "property_manager"].includes(user?.role);

    const doDelete = async (g, e) => {
        e.stopPropagation();  // don't open drawer
        const label = `${g.first_name} ${g.last_name}`.trim() || g.email || g.id;
        if (!window.confirm(`${t("guests.delete_confirm")} — ${label}\n\n${t("guests.delete_confirm_desc")}`)) return;
        try {
            await api.delete(`/guests/${g.id}`);
            toast.success(t("guests.deleted"));
            onReload && onReload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    return (
        <div className="akm-card overflow-hidden">
            <table className="akm-table w-full">
                <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="px-3 py-2 text-left">{t("guests.cols.name")}</th>
                        <th className="px-3 py-2 text-left">{t("guests.cols.contact")}</th>
                        <th className="px-3 py-2 text-left">{t("guests.cols.segments")}</th>
                        <th className="px-3 py-2 text-right">{t("guests.cols.stays")}</th>
                        <th className="px-3 py-2 text-right">{t("guests.cols.lifetime")}</th>
                        <th className="px-3 py-2 text-left">{t("guests.cols.loyalty")}</th>
                        <th className="px-3 py-2 text-left">{t("guests.cols.last_stay")}</th>
                        {canDelete && <th className="w-8 px-3 py-2"></th>}
                    </tr>
                </thead>
                <tbody data-testid="guests-table-body">
                    {guests.map((g) => (
                        <tr key={g.id} onClick={() => onOpen(g.id)}
                            className="cursor-pointer border-t border-border hover:bg-muted/40"
                            data-testid={`guest-row-${g.id}`}>
                            <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                    <div className="font-medium">{g.first_name} {g.last_name}</div>
                                    {g.vip && <Star size={12} className="fill-amber-400 text-amber-500" />}
                                    {g.blacklisted && <Ban size={12} className="text-red-600" />}
                                    {g.company && <span className="rounded-full bg-violet-50 px-1.5 py-0 text-[10px] font-medium text-violet-700">{g.company}</span>}
                                </div>
                                {g.tags?.length > 0 && (
                                    <div className="mt-0.5 flex flex-wrap gap-1">
                                        {g.tags.slice(0, 4).map((t) => (
                                            <span key={t} className="rounded-full bg-muted px-1.5 py-0 text-[10px] text-muted-foreground">{t}</span>
                                        ))}
                                    </div>
                                )}
                            </td>
                            <td className="px-3 py-2 text-[12px] text-muted-foreground">
                                <div className="truncate max-w-[200px]">{g.email || "—"}</div>
                                <div className="text-[11px]">{g.phone || "—"}</div>
                            </td>
                            <td className="px-3 py-2">
                                <div className="flex flex-wrap gap-1">
                                    {(g.segments || []).map((s) => (
                                        <span key={s} className={`rounded-full px-1.5 py-0 text-[10px] font-medium ${SEGMENT_BADGE[s] || "bg-muted text-muted-foreground"}`}>
                                            {s.replace("_", " ")}
                                        </span>
                                    ))}
                                    {(g.segments || []).length === 0 && <span className="text-[11px] text-muted-foreground">—</span>}
                                </div>
                            </td>
                            <td className="px-3 py-2 text-right">{g.total_stays}</td>
                            <td className="px-3 py-2 text-right font-medium">{formatCurrency(g.lifetime_value || 0)}</td>
                            <td className="px-3 py-2">
                                <span className={`text-[12px] capitalize ${LOYALTY_TIER_COLOR[g.loyalty_tier] || "text-muted-foreground"}`}>
                                    {g.loyalty_tier} · {g.loyalty_points || 0}p
                                </span>
                            </td>
                            <td className="px-3 py-2 text-[12px] text-muted-foreground">
                                {g.last_stay_at ? formatDate(g.last_stay_at) : "—"}
                            </td>
                            {canDelete && (
                                <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        onClick={(e) => doDelete(g, e)}
                                        className="text-muted-foreground hover:text-red-600"
                                        title={t("guests.delete")}
                                        data-testid={`guest-delete-${g.id}`}
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                </td>
                            )}
                        </tr>
                    ))}
                    {guests.length === 0 && (
                        <tr><td colSpan={canDelete ? 8 : 7} className="py-12 text-center text-muted-foreground">{t("guests.empty")}</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

// ------------------------------------------------------------
// Drawer (360° profile)
// ------------------------------------------------------------
const DRAWER_TAB_KEYS = ["profile", "stays", "notes", "complaints", "loyalty", "communications"];

function GuestDrawer({ guestId, onClose, onReload }) {
    const { t } = useTranslation();
    const [tab, setTab] = useState("profile");
    const [profile, setProfile] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get(`/guests/${guestId}/profile`);
            setProfile(data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [guestId]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    if (!profile) {
        return (
            <div className="fixed inset-0 z-50 flex justify-end">
                <div className="absolute inset-0 bg-foreground/30" onClick={onClose} />
                <div className="relative h-full w-full max-w-[720px] bg-background p-8 text-sm text-muted-foreground">{t("guests.loading_ellipsis")}</div>
            </div>
        );
    }
    const g = profile.guest;
    return (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="guest-drawer">
            <div className="absolute inset-0 bg-foreground/30" onClick={onClose} />
            <div className="relative h-full w-full max-w-[820px] overflow-y-auto bg-background shadow-xl">
                {/* Header */}
                <div className="sticky top-0 z-10 border-b border-border bg-background/95 px-5 py-3.5 backdrop-blur">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2">
                                <div className="text-base font-semibold tracking-tight">{g.first_name} {g.last_name}</div>
                                {g.vip && <Star size={14} className="fill-amber-400 text-amber-500" />}
                                {g.blacklisted && <Ban size={14} className="text-red-600" />}
                            </div>
                            <div className="mt-0.5 text-[12px] text-muted-foreground">
                                {g.email || "—"} · {g.phone || "—"} · {g.country || "—"}
                            </div>
                            <div className="mt-1 flex flex-wrap gap-1">
                                {(g.segments || []).map((s) => (
                                    <span key={s} className={`rounded-full px-1.5 py-0 text-[10px] font-medium ${SEGMENT_BADGE[s] || "bg-muted text-muted-foreground"}`}>
                                        {t(`guests.segments.${s}`, s.replace("_", " "))}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-[11px] text-muted-foreground">{t("guests.drawer.lifetime_value")}</div>
                            <div className="text-lg font-semibold text-emerald-700">{formatCurrency(g.lifetime_value || 0)}</div>
                            <div className="mt-0.5 text-[11px] text-muted-foreground">
                                {t("guests.drawer.stays_pts_tier", { stays: g.total_stays, pts: g.loyalty_points || 0, tier: g.loyalty_tier })}
                            </div>
                            <button onClick={onClose} className="akm-btn-ghost mt-2 text-[11px]">{t("guests.drawer.close")}</button>
                        </div>
                    </div>
                    <div className="mt-3 flex items-center gap-1 border-b border-border">
                        {DRAWER_TAB_KEYS.map((k) => (
                            <button key={k} onClick={() => setTab(k)}
                                    data-testid={`drawer-tab-${k}`}
                                    className={`-mb-px border-b-2 px-3 py-2 text-[12px] font-medium transition ${
                                        tab === k ? "border-[#ff7b02] text-[#ff7b02]" : "border-transparent text-muted-foreground hover:text-foreground"
                                    }`}>
                                {t(`guests.drawer.${k}`)}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="px-5 py-4">
                    {tab === "profile" && <ProfilePane g={g} reload={async () => { await load(); await onReload(); }} />}
                    {tab === "stays" && <StaysPane stays={profile.reservations} />}
                    {tab === "notes" && <NotesPane guestId={g.id} notes={profile.notes} reload={load} />}
                    {tab === "complaints" && <ComplaintsPane guestId={g.id} items={profile.complaints} reload={load} />}
                    {tab === "loyalty" && <LoyaltyPane guestId={g.id} guest={g} ledger={profile.loyalty_ledger} reload={async () => { await load(); await onReload(); }} />}
                    {tab === "communications" && <CommunicationsPane guestId={g.id} items={profile.communications} reload={load} />}
                </div>
            </div>
        </div>
    );
}

function ProfilePane({ g, reload }) {
    const { t } = useTranslation();
    const [form, setForm] = useState(g);
    useEffect(() => setForm(g), [g.id]); // eslint-disable-line

    const save = async () => {
        try {
            await api.patch(`/guests/${g.id}`, form);
            toast.success(t("guests.profile.saved"));
            await reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const setTags = (raw) => {
        const tags = raw.split(",").map((s) => s.trim()).filter(Boolean);
        setForm({ ...form, tags });
    };

    return (
        <div className="space-y-4" data-testid="profile-pane">
            <div className="grid grid-cols-2 gap-3">
                <Field label={t("guests.profile.first_name")}>
                    <input className="akm-input" value={form.first_name || ""}
                           onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                </Field>
                <Field label={t("guests.profile.last_name")}>
                    <input className="akm-input" value={form.last_name || ""}
                           onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                </Field>
                <Field label={t("guests.profile.email")}>
                    <input className="akm-input" type="email" value={form.email || ""}
                           onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </Field>
                <Field label={t("guests.profile.phone")}>
                    <PhoneInput value={form.phone || ""}
                                onChange={(v) => setForm({ ...form, phone: v })}
                                defaultCountry="AL"
                                testid="guest-phone" />
                </Field>
                <Field label={t("guests.profile.country")}>
                    <input className="akm-input" value={form.country || ""}
                           onChange={(e) => setForm({ ...form, country: e.target.value })} />
                </Field>
                <Field label={t("guests.profile.company")}>
                    <input className="akm-input" value={form.company || ""}
                           onChange={(e) => setForm({ ...form, company: e.target.value })}
                           data-testid="profile-company" />
                </Field>
            </div>

            <Field label={t("guests.profile.tags")}>
                <input className="akm-input" value={(form.tags || []).join(", ")}
                       onChange={(e) => setTags(e.target.value)}
                       placeholder={t("guests.profile.tags_placeholder")}
                       data-testid="profile-tags" />
            </Field>

            <div className="grid grid-cols-3 gap-2">
                <label className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-[12px] cursor-pointer">
                    <input type="checkbox" checked={!!form.vip}
                           onChange={(e) => setForm({ ...form, vip: e.target.checked })}
                           data-testid="profile-vip" />
                    <Star size={12} className="text-amber-500" /> {t("guests.profile.vip")}
                </label>
                <label className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-[12px] cursor-pointer">
                    <input type="checkbox" checked={!!form.blacklisted}
                           onChange={(e) => setForm({ ...form, blacklisted: e.target.checked })}
                           data-testid="profile-blacklist" />
                    <Ban size={12} className="text-red-600" /> {t("guests.profile.blacklisted")}
                </label>
                <label className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-[12px] cursor-pointer">
                    <input type="checkbox" checked={!!form.marketing_consent}
                           onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })}
                           data-testid="profile-marketing" />
                    <Mail size={12} className="text-emerald-700" /> {t("guests.profile.marketing_consent")}
                </label>
            </div>

            <Field label={t("guests.profile.internal_notes")}>
                <textarea className="akm-input" rows="3" value={form.notes || ""}
                          onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>

            <div className="flex justify-end">
                <button onClick={save} className="akm-btn-primary" data-testid="profile-save">{t("guests.profile.save")}</button>
            </div>
        </div>
    );
}

function StaysPane({ stays }) {
    const { t } = useTranslation();
    if (!stays.length) {
        return <div className="rounded-md border border-dashed border-border bg-card/40 py-10 text-center text-sm text-muted-foreground">{t("guests.notes.no_stays")}</div>;
    }
    return (
        <div className="akm-card overflow-hidden" data-testid="stays-pane">
            <table className="w-full text-sm">
                <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="px-3 py-2 text-left">{t("guests.cols.stay")}</th>
                        <th className="px-3 py-2 text-left">{t("guests.cols.source")}</th>
                        <th className="px-3 py-2 text-left">{t("guests.cols.status")}</th>
                        <th className="px-3 py-2 text-right">{t("guests.cols.total")}</th>
                        <th className="px-3 py-2 text-right">{t("guests.cols.paid")}</th>
                    </tr>
                </thead>
                <tbody>
                    {stays.map((r) => (
                        <tr key={r.id} className="border-t border-border">
                            <td className="px-3 py-2">
                                <div className="font-medium">{r.check_in} → {r.check_out}</div>
                                <div className="text-[11px] text-muted-foreground">{r.adults} adult{r.adults > 1 ? "s" : ""}{r.children ? ` · ${r.children} children` : ""}</div>
                            </td>
                            <td className="px-3 py-2 text-[12px] capitalize text-muted-foreground">{r.source.replace("_", " ")}</td>
                            <td className="px-3 py-2 text-[12px] capitalize">{r.status.replace("_", " ")}</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(r.total_amount || 0)}</td>
                            <td className="px-3 py-2 text-right text-muted-foreground">{formatCurrency(r.paid_amount || 0)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function NotesPane({ guestId, notes, reload }) {
    const { t } = useTranslation();
    const [text, setText] = useState("");
    const [kind, setKind] = useState("general");
    const add = async () => {
        if (!text.trim()) return;
        try {
            await api.post(`/guests/${guestId}/notes`, { text, kind });
            setText(""); setKind("general");
            await reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    return (
        <div className="space-y-3" data-testid="notes-pane">
            <div className="akm-card p-3 space-y-2">
                <textarea className="akm-input" rows="2" value={text} placeholder={t("guests.notes.placeholder")}
                          onChange={(e) => setText(e.target.value)} data-testid="note-text" />
                <div className="flex items-center gap-2">
                    <select className="akm-input max-w-[180px]" value={kind} onChange={(e) => setKind(e.target.value)}>
                        <option value="general">{t("guests.notes.general")}</option>
                        <option value="preference">{t("guests.notes.preference")}</option>
                        <option value="special_request">{t("guests.notes.special_request")}</option>
                    </select>
                    <button onClick={add} className="akm-btn-primary ml-auto" data-testid="note-submit">{t("guests.notes.add_note")}</button>
                </div>
            </div>
            <div className="space-y-2">
                {notes.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">{t("guests.notes.empty")}</div>}
                {notes.map((n) => (
                    <div key={n.id} className="akm-card p-3" data-testid={`note-${n.id}`}>
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                            <span>{n.author_name || t("guests.notes.staff")} · {formatDate(n.created_at)}</span>
                            <span className="rounded-full bg-muted px-1.5 py-0 capitalize">{n.kind.replace("_", " ")}</span>
                        </div>
                        <div className="mt-1 text-sm">{n.text}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ComplaintsPane({ guestId, items, reload }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const setStatus = async (id, status) => {
        try {
            await api.patch(`/guests/${guestId}/complaints/${id}`, { status });
            await reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    return (
        <div className="space-y-3" data-testid="complaints-pane">
            <div className="flex justify-end">
                <button onClick={() => setOpen(true)} className="akm-btn-primary" data-testid="complaint-new">
                    <Plus size={13} /> {t("guests.complaint.log")}
                </button>
            </div>
            {items.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">{t("guests.complaint.no_items")}</div>}
            {items.map((c) => (
                <div key={c.id} className="akm-card p-3" data-testid={`complaint-${c.id}`}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 text-sm font-semibold">
                                {c.severity === "high" && <AlertTriangle size={13} className="text-red-600" />}
                                {c.subject}
                            </div>
                            <div className="mt-1 text-[12px] text-muted-foreground">{c.description}</div>
                            <div className="mt-1 text-[11px] text-muted-foreground">{c.author_name || t("guests.notes.staff")} · {formatDate(c.created_at)}</div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${
                                c.status === "resolved" ? "bg-emerald-50 text-emerald-700" :
                                c.status === "in_progress" ? "bg-blue-50 text-blue-700" :
                                "bg-amber-50 text-amber-800"
                            }`}>{c.status.replace("_", " ")}</span>
                            {c.status !== "resolved" && (
                                <button onClick={() => setStatus(c.id, c.status === "open" ? "in_progress" : "resolved")}
                                        className="akm-btn-ghost text-[11px]">
                                    → {c.status === "open" ? t("guests.complaint.in_progress") : t("guests.complaint.resolved")}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            ))}
            {open && <ComplaintForm guestId={guestId} onClose={() => setOpen(false)} onSaved={async () => { setOpen(false); await reload(); }} />}
        </div>
    );
}

function ComplaintForm({ guestId, onClose, onSaved }) {
    const { t } = useTranslation();
    const [f, setF] = useState({ subject: "", description: "", severity: "normal" });
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post(`/guests/${guestId}/complaints`, f);
            toast.success(t("guests.complaint.saved"));
            onSaved();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setSaving(false); }
    };
    return (
        <Modal title={t("guests.complaint.log")} onClose={onClose}>
            <form onSubmit={submit} className="space-y-3">
                <Field label={t("guests.complaint.subject")}><input required className="akm-input" value={f.subject}
                                              onChange={(e) => setF({ ...f, subject: e.target.value })}
                                              data-testid="complaint-subject" /></Field>
                <Field label={t("guests.complaint.description")}><textarea className="akm-input" rows="3" value={f.description}
                                                     onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
                <Field label={t("guests.complaint.severity")}>
                    <select className="akm-input" value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })}>
                        <option value="low">{t("guests.complaint.severity_low")}</option>
                        <option value="normal">{t("guests.complaint.severity_normal")}</option>
                        <option value="high">{t("guests.complaint.severity_high")}</option>
                    </select>
                </Field>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="akm-btn-ghost">{t("common.cancel")}</button>
                    <button type="submit" disabled={saving} className="akm-btn-primary" data-testid="complaint-submit">{saving ? t("common.saving") : t("guests.complaint.log")}</button>
                </div>
            </form>
        </Modal>
    );
}

function LoyaltyPane({ guestId, guest, ledger, reload }) {
    const { t } = useTranslation();
    const [amount, setAmount] = useState(50);
    const [reason, setReason] = useState("Stay reward");
    const txn = async (kind) => {
        try {
            const amt = kind === "redeem" ? -Math.abs(amount) : Math.abs(amount);
            await api.post(`/guests/${guestId}/loyalty`, { amount: amt, reason, kind });
            toast.success(kind === "redeem" ? t("guests.loyalty.redeemed_pts", { amt: Math.abs(amt) }) : t("guests.loyalty.earned_pts", { amt: Math.abs(amt) }));
            await reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    return (
        <div className="space-y-3" data-testid="loyalty-pane">
            <div className="akm-card p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("guests.loyalty.balance")}</div>
                        <div className="text-2xl font-semibold">{guest.loyalty_points || 0} <span className="text-base text-muted-foreground">pts</span></div>
                    </div>
                    <div className="text-right">
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("guests.loyalty.tier")}</div>
                        <div className={`text-lg font-semibold capitalize ${LOYALTY_TIER_COLOR[guest.loyalty_tier] || "text-foreground"}`}>{guest.loyalty_tier}</div>
                    </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                    <input type="number" className="akm-input" value={amount}
                           onChange={(e) => setAmount(parseInt(e.target.value, 10) || 0)}
                           data-testid="loyalty-amount" />
                    <input className="akm-input" value={reason} onChange={(e) => setReason(e.target.value)}
                           placeholder={t("guests.loyalty.reason_placeholder")} />
                </div>
                <div className="mt-2 flex justify-end gap-2">
                    <button onClick={() => txn("redeem")} className="akm-btn-ghost text-[11px]" data-testid="loyalty-redeem">{t("guests.loyalty.redeem")}</button>
                    <button onClick={() => txn("earn")} className="akm-btn-primary text-[11px]" data-testid="loyalty-earn">{t("guests.loyalty.earn")}</button>
                </div>
            </div>
            <div className="space-y-1">
                {ledger.length === 0 && <div className="py-4 text-center text-sm text-muted-foreground">{t("guests.loyalty.no_txn")}</div>}
                {ledger.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-1.5 text-[12px]">
                        <div>
                            <span className={`mr-2 font-mono font-semibold ${tx.amount > 0 ? "text-emerald-700" : "text-red-700"}`}>
                                {tx.amount > 0 ? "+" : ""}{tx.amount}
                            </span>
                            {tx.reason}
                            <span className="ml-2 text-[10px] text-muted-foreground">{t("guests.loyalty.by")} {tx.author_name || "—"}</span>
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                            {formatDate(tx.created_at)} · {t("guests.loyalty.balance_after")} {tx.balance_after}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function CommunicationsPane({ guestId, items, reload }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const iconFor = (type) => type === "email" ? Mail : type === "whatsapp" ? MessageCircle : type === "phone" ? Phone : ClipboardList;
    return (
        <div className="space-y-2" data-testid="communications-pane">
            <div className="flex justify-end">
                <button onClick={() => setOpen(true)} className="akm-btn-primary" data-testid="comm-new">
                    <Plus size={13} /> {t("guests.comm.log")}
                </button>
            </div>
            {items.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">{t("guests.comm.empty")}</div>}
            {items.map((c) => {
                const Icon = iconFor(c.type);
                return (
                    <div key={c.id} className="akm-card p-3" data-testid={`comm-${c.id}`}>
                        <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                            <Icon size={12} /> <span className="capitalize">{c.type}</span> · {c.to || "—"} · {formatDate(c.created_at)}
                            {c.status && <span className="ml-auto rounded-full bg-muted px-1.5 py-0 text-[10px]">{c.status.replace("_", " ")}</span>}
                        </div>
                        {c.subject && <div className="mt-1 text-sm font-medium">{c.subject}</div>}
                        {c.body && <div className="mt-0.5 text-[12px] text-muted-foreground">{c.body}</div>}
                    </div>
                );
            })}
            {open && <CommForm guestId={guestId} onClose={() => setOpen(false)} onSaved={async () => { setOpen(false); await reload(); }} />}
        </div>
    );
}

function CommForm({ guestId, onClose, onSaved }) {
    const { t } = useTranslation();
    const [f, setF] = useState({ type: "email", subject: "", body: "", status: "logged" });
    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/guests/${guestId}/communications`, f);
            toast.success(t("guests.comm.saved"));
            onSaved();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };
    return (
        <Modal title={t("guests.comm.log")} onClose={onClose}>
            <form onSubmit={submit} className="space-y-3">
                <Field label={t("guests.comm.type")}>
                    <select className="akm-input" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
                        <option value="email">{t("guests.comm.email")}</option>
                        <option value="whatsapp">{t("guests.comm.whatsapp")}</option>
                        <option value="phone">{t("guests.comm.phone")}</option>
                        <option value="survey">{t("guests.comm.survey")}</option>
                    </select>
                </Field>
                <Field label={t("guests.comm.subject")}><input className="akm-input" value={f.subject}
                                               onChange={(e) => setF({ ...f, subject: e.target.value })} /></Field>
                <Field label={t("guests.comm.body")}><textarea className="akm-input" rows="3" value={f.body}
                                                         onChange={(e) => setF({ ...f, body: e.target.value })} /></Field>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="akm-btn-ghost">{t("common.cancel")}</button>
                    <button type="submit" className="akm-btn-primary" data-testid="comm-submit">{t("guests.comm.log")}</button>
                </div>
            </form>
        </Modal>
    );
}

// ------------------------------------------------------------
// New guest form (creates with VIP/blacklist/tags)
// ------------------------------------------------------------
function GuestForm({ onClose, onSaved }) {
    const { t } = useTranslation();
    const [form, setForm] = useState({
        first_name: "", last_name: "", email: "", phone: "", country: "",
        company: "", vip: false, blacklisted: false, marketing_consent: false,
        tags: [],
    });
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const body = { ...form };
            if (!body.email) delete body.email;
            await api.post("/guests", body);
            toast.success(t("guests.new.created"));
            onSaved();
        } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
        finally { setSaving(false); }
    };
    return (
        <Modal title={t("guests.new.title")} onClose={onClose}>
            <form onSubmit={submit} className="space-y-3" data-testid="guest-form">
                <div className="grid grid-cols-2 gap-2">
                    <Field label={t("guests.profile.first_name")}><input className="akm-input" required value={form.first_name}
                                                     onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></Field>
                    <Field label={t("guests.profile.last_name")}><input className="akm-input" required value={form.last_name}
                                                    onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></Field>
                </div>
                <Field label={t("guests.profile.email")}><input type="email" className="akm-input" value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                <div className="grid grid-cols-2 gap-2">
                    <Field label={t("guests.profile.phone")}><PhoneInput value={form.phone}
                                                    onChange={(v) => setForm({ ...form, phone: v })}
                                                    defaultCountry="AL"
                                                    testid="new-guest-phone" /></Field>
                    <Field label={t("guests.profile.country")}><input className="akm-input" value={form.country}
                                                  onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
                </div>
                <Field label={t("guests.new.company_hint")}>
                    <input className="akm-input" value={form.company}
                           onChange={(e) => setForm({ ...form, company: e.target.value })} />
                </Field>
                <Field label={t("guests.profile.tags")}>
                    <input className="akm-input"
                           onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                           placeholder={t("guests.new.tags_hint")} />
                </Field>
                <div className="grid grid-cols-3 gap-2">
                    <label className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-[12px]">
                        <input type="checkbox" checked={form.vip}
                               onChange={(e) => setForm({ ...form, vip: e.target.checked })} /> {t("guests.profile.vip")}
                    </label>
                    <label className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-[12px]">
                        <input type="checkbox" checked={form.blacklisted}
                               onChange={(e) => setForm({ ...form, blacklisted: e.target.checked })} /> {t("guests.profile.blacklisted")}
                    </label>
                    <label className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-[12px]">
                        <input type="checkbox" checked={form.marketing_consent}
                               onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} /> {t("guests.new.consent")}
                    </label>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="akm-btn-ghost">{t("common.cancel")}</button>
                    <button type="submit" disabled={saving} className="akm-btn-primary" data-testid="guest-submit">{saving ? t("common.saving") : t("common.create")}</button>
                </div>
            </form>
        </Modal>
    );
}
